<script setup>
import {computed, reactive, inject, ref, onMounted, watch, provide} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {discordRegistrationTime} from "@/utils/discordRegistrationTime.js";
import {formatPrice} from "@/utils/priceFormatter.js";
import {cleanObject} from "@/utils/cleanObject.js";
//import components
import {Icon} from "@iconify/vue";
import {
  Dialog,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  InputGroup,
  InputGroupAddon,
  InputText,
  FloatLabel,
  Select,
  Textarea,
  InputNumber,
  Rating,
  DatePicker
} from "primevue";
import Attachments from "@/views/orders/Details/Attachments.vue";
import Users from "@/components/Users.vue";
import UserProfile from "@/components/UserProfile/index.vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useVuelidate} from "@vuelidate/core";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {useRouter} from "vue-router";
import {showMessage} from "@/utils/showMessage.js";
import {useHotOrdersStore} from "@/stores/hotOrders.js";

//stores
const hotOrdersStore = useHotOrdersStore();
const userStore = useUserStore();
const currencyPricesStore = useCurrencyPricesStore();
const ordersStore = useMainOrdersStore()

//loading flags
const {
  saving,
  fetching,
  setSaving,
  setFetching,
} = useLoadingFlags()
const isUploadingAttachment = ref(false)
const isShowUserProfileModal = ref(false);

//other composables
const {isXL} = useBreakpoint();
const toast = useToast();
const router = useRouter()

//emits
const emit = defineEmits(['save', 'refreshHotOrders']);

//props
const props = defineProps({
  hotorder: {
    type: Object,
    required: true
  }
})

//injects
const visible = inject('isShowUpdateUpdateDogModal')

//component variables
const intervalOptions = [
  {value: 0, label: 'One-time only'},
  {value: 3600000, label: 'Every 1 hour'},
  {value: 3600000 * 2, label: 'Every 2 hours'},
  {value: 3600000 * 4, label: 'Every 4 hours'},
  {value: 3600000 * 6, label: 'Every 6 hours'},
  {value: 3600000 * 8, label: 'Every 8 hours'},
  {value: 3600000 * 12, label: 'Every 12 hours'},
  {value: 3600000 * 24, label: 'Every 24 hours'},
]
const states = ['New', 'Pending', 'Completed', 'Off']
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Pending: 'bg-[#FF9800]',
  Completed: 'bg-[#4CAF50]',
  Off: 'bg-[#F44336]'
}

const rating = ref(null)
const tags = ref([])
const form = reactive({
  "orderID": props.hotorder.id ? props.hotorder.id : '',
  "fireTime": props.hotorder.updatedog.fireTime ? props.hotorder.updatedog.fireTime : Date.now(),
  "interval": props.hotorder.updatedog.interval || props.hotorder.updatedog.interval === 0 ? props.hotorder.updatedog.interval : null,
  "state": props.hotorder.updatedog.state ? props.hotorder.updatedog.state : null,
})
const rules = computed(() => {
  return {
    orderID: {required},
    fireTime: {required},
    state: {required},
  };
});
const $vuelidate = useVuelidate(rules, form);

const fireTimeModel = computed({
  get() {
    if (form.fireTime && !isNaN(form.fireTime)) {
      return new Date(Number(form.fireTime));
    }
    return null;
  },
  set(value) {
    if (value instanceof Date) {
      form.fireTime = value.getTime();
    } else {
      form.fireTime = '';
    }
  }
})

//component functions
function selectNow() {
  const now = new Date();
  form.fireTime = now.getTime();
}

function clearFireTime() {
  form.fireTime = '';
}

const onSave = async () => {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }

  setSaving(true)
  try {
    const res = await hotOrdersStore.action_updateUpdateDog(form)

    if (res.result === true) {
      showMessage('UpdateDog Updated!', 'success');
      emit('refreshHotOrders')
      visible.value = false
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
  }
  setSaving(false)
}

//hooks
onMounted(() => {

})

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        Edit Update Dog
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-6 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="hotOrderID" v-model="form.orderID" autocomplete="off" disabled readonly
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700 p-filled"
                       :class="{ 'has-error': $vuelidate.orderID.$error }"/>
            <label for="hotOrderID" class="!bg-slate-900"
                   :class="{ 'focus:text-red-300': $vuelidate.orderID.$error }">Order ID</label>
          </FloatLabel>
          <template v-if="$vuelidate.orderID.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vuelidate.orderID.required.$response">
              This field is required
            </p>
          </template>
        </div>
        <div class="col-span-6">
          <FloatLabel
              class="w-full custom-select"
              :class="{ 'has-error': $vuelidate.state.$error }"
              variant="on"
          >
            <Select
                v-model="form.state"
                inputId="state"
                :options="states"
                class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                :class="[$vuelidate.state.$error ? '!border !border-red-300' : '!border-slate-700']"
                input-class="!py-1 !items-center !flex !pr-0"
                overlay-class="select-overlay-in-modal h-max"
            >
              <template #value="slotProps">
                <div class="flex items-center gap-2 !text-xs">
                  <span class="w-2 h-2 rounded-full" :class="stateColorsMap[slotProps.value]"/>
                  {{ slotProps.value }}
                </div>
              </template>

              <template #option="slotProps">
                <div class="flex items-center gap-2 !text-xs 2xl:!text-sm">
                  <span class="w-2 h-2 rounded-full" :class="stateColorsMap[slotProps.option]"/>
                  {{ slotProps.option }}
                </div>
              </template>
            </Select>
          </FloatLabel>
        </div>
        <div class="col-span-6 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <DatePicker
                inputId="fireTime"
                v-model="fireTimeModel"
                showTime
                hourFormat="24"
                dateFormat="d M yy"
                fluid
                :showButtonBar="true"
                class="flex-1"
                size="small"
                :input-class="['!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700', $vuelidate.fireTime.$error ? 'has-error' : '']"
                ref="pickerRef"
                showIcon
                iconDisplay="input"
            >
              <template #todaybutton>
                <span @click="selectNow"
                      class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50">Now</span>
              </template>
              <template #clearbutton>
                <span @click="clearFireTime"
                      class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50">Clear</span>
              </template>
            </DatePicker>
            <label for="fireTime" class="!bg-slate-900">Fire Time</label>
          </FloatLabel>
          <template v-if="$vuelidate.fireTime.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vuelidate.fireTime.required.$response">
              This field is required
            </p>
          </template>
        </div>
        <div class="col-span-6 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel class="w-full custom-select"
                      variant="on">
            <Select v-model="form.interval" inputId="checkup" :options="intervalOptions"
                    optionLabel="label"
                    option-value="value"
                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                    input-class="!text-xs 2xl:!text-sm"
                    :virtualScrollerOptions="{ itemSize: 38 }"
                    filter autoFilterFocus/>
            <label for="checkup" class="!bg-slate-900">Interval</label>
          </FloatLabel>
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-3">
      <button @click="onSave"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              :disabled="saving">
        Save
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
      </button>
    </div>
  </Dialog>
</template>
<style scoped>

</style>